import { Footer } from '@latitude/footer';
import { cn } from '@latitude/token-utils';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { SheetContent } from 'components/SheetContent';
import { SheetFooter } from 'components/SheetFooter';
import { SheetHeader } from 'components/SheetHeader';
import { DRAWER_WIDTHS } from 'drawerSizes';
import { useEffect, useRef } from 'react';
import type { DrawerProps, DrawerSize } from 'types';

export type { DrawerProps, DrawerSize };

const Sheet = SheetPrimitive.Root;

export const SheetTrigger = SheetPrimitive.Trigger;

const TRANSITION_STYLE = {
  transitionProperty:
    'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, left',
};

export const Drawer: React.FC<DrawerProps> = ({
  title,
  description,
  trigger,
  footer,
  footerProps,
  children,
  open,
  defaultOpen,
  onOpenChange,
  side = 'right',
  size = 'medium',
  width,
  height = 300,
  testId = 'drawer-content',
  extraHeaderContent,
  onInteractOutside,
  contentStyles,
  hideOverlay = false,
  bluredOverlay = true,
  portalContent = true,
  fixed = true,
  hideHeader = false,
  className = '',
  modal = true,
  onOpenAutoFocus,
  onMouseEnter,
  onMouseLeave,
  header = {},
}) => {
  // Radix adds pointer events none to the body which beaks a lot of components
  // https://github.com/radix-ui/primitives/issues/2122#issuecomment-2205128989
  useEffect(() => {
    if (open) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = 'auto';
    }
  }, [open]);

  // The control focused when the drawer opened. Radix returns focus to the
  // Dialog's Trigger on close, and a drawer controlled through `open` has
  // none to return to, so a modal drawer restores its opener here or focus
  // falls back to body. A drawer with a `trigger` keeps Radix's return, and
  // a non-modal one (the sidebar) its own focus handling.
  const openerRef = useRef<HTMLElement | null>(null);
  const restoresOpener = !trigger && modal;

  const widthStyle = {
    ...TRANSITION_STYLE,
    ...contentStyles,
    width: `${width ?? DRAWER_WIDTHS[size]}px`,
    maxWidth: 'none',
  };
  const heightStyle = {
    ...TRANSITION_STYLE,
    ...contentStyles,
    height: `${height}px`,
    maxWidth: 'none',
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
      modal={modal}
    >
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      <SheetContent
        // forceMount is true | undefined for some reason. Only meaningful when
        // portalled: forcing mount on non-portalled content stops Radix from
        // ever reporting the dialog as open (@radix-ui/react-dialog 1.1.23).
        forceMount={!fixed && portalContent ? true : undefined}
        data-testid={testId}
        side={side}
        className={className}
        style={side === 'right' || side === 'left' ? widthStyle : heightStyle}
        onInteractOutside={onInteractOutside}
        hideOverlay={hideOverlay}
        bluredOverlay={bluredOverlay}
        portalContent={portalContent}
        fixed={fixed}
        onOpenAutoFocus={(event) => {
          // Fired before Radix moves focus into the drawer.
          openerRef.current = document.activeElement as HTMLElement | null;
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={
          restoresOpener
            ? (event) => {
                event.preventDefault();
                openerRef.current?.focus({ preventScroll: true });
              }
            : undefined
        }
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {!hideHeader && (
          <SheetHeader
            {...header}
            title={title}
            description={description}
            extraHeaderContent={extraHeaderContent}
          />
        )}
        <div
          className={cn(
            'h-full overflow-auto',
            hideHeader
              ? 'p-spacing-sys-spacing-16'
              : 'px-spacing-sys-spacing-16',
          )}
        >
          {children}
        </div>
        {footerProps ? (
          <Footer data-testid="drawer-footer" {...footerProps} />
        ) : (
          footer && <SheetFooter>{footer}</SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
